/**
 * OnboardingSlide Component
 * 
 * A single slide in the onboarding carousel with Lottie animation and text.
 */

import React from "react";
import { View, Text, Dimensions } from "react-native";
import LottieView from "lottie-react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import type { OnboardingSlide as OnboardingSlideType } from "../types";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

interface OnboardingSlideProps {
  /** The slide data */
  slide: OnboardingSlideType;
  /** Whether this is the current active slide */
  isActive?: boolean;
}

/**
 * Single onboarding slide component
 */
export const OnboardingSlide: React.FC<OnboardingSlideProps> = ({
  slide,
  isActive = true,
}) => {
  const colors = useThemeColors();

  return (
    <View 
      className="flex-1 items-center justify-center px-8"
      style={{ width: SCREEN_WIDTH }}
    >
      {/* Lottie Animation */}
      <View 
        className="items-center justify-center mb-8"
        style={{
          width: SCREEN_WIDTH * 0.7,
          height: SCREEN_HEIGHT * 0.3,
        }}
      >
        <LottieView
          source={slide.lottieSource}
          autoPlay={isActive}
          loop
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      </View>

      {/* Icon Badge (optional) */}
      {slide.icon && (
        <View 
          className="w-14 h-14 rounded-full items-center justify-center mb-6"
          style={{ backgroundColor: `${colors.primary}15` }}
        >
          <Ionicons 
            name={slide.icon as any} 
            size={28} 
            color={colors.primary} 
          />
        </View>
      )}

      {/* Title */}
      <Text 
        className="text-2xl font-bold text-center mb-4"
        style={{ color: colors.text }}
      >
        {slide.title}
      </Text>

      {/* Description */}
      <Text 
        className="text-base text-center leading-relaxed"
        style={{ color: colors.textSecondary }}
      >
        {slide.description}
      </Text>
    </View>
  );
};

export default OnboardingSlide;

